import { useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Input } from '@teach/ui'
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog'
import { usePatchMeMutation } from '@/features/auth/authApi'
import { useIsMobile } from '@/hooks/use-mobile'
import { useMessages, useUiLang } from '@/i18n'
import { countryName, filterCountries, useCountriesQuery } from '../countriesApi'
import { messages } from './messages'

/**
 * На телефоне клавиатура от фокуса в поиске сразу закрыла бы половину шторки
 * со списком. Но и просто отменить автофокус нельзя: фокус остался бы на
 * кнопке за шторкой, которую Radix прячет от диктора (`aria-hidden`). Фокус
 * получает сама шторка — у неё `tabIndex=-1`, клавиатура не поднимается.
 */
const focusSheetItself = (event: Event) => {
  event.preventDefault()
  ;(event.currentTarget as HTMLElement | null)?.focus()
}

interface PickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  current: string | null
}

/** Полный список с поиском, как `CountryPickerModal` веб-академии. Выбор сохраняется сразу. */
export const CountryPickerDialog = ({ open, onOpenChange, current }: PickerProps) => {
  const m = useMessages(messages)
  const lang = useUiLang()
  const [query, setQuery] = useState('')
  const isMobile = useIsMobile()
  const {
    data: countries = [],
    isLoading: isListLoading,
    isError,
    refetch,
  } = useCountriesQuery(undefined, { skip: !open })
  const [patchMe, { isLoading }] = usePatchMeMutation()
  const shown = filterCountries(countries, query)

  const close = (next: boolean) => {
    if (!next) setQuery('')
    onOpenChange(next)
  }

  const pick = async (code: string) => {
    if (code === current) return close(false)
    try {
      // Ровно `{ country }`: язык рядом перезаписал бы язык профиля (см. patchMe)
      await patchMe({ country: code }).unwrap()
      close(false)
    } catch {
      toast.error(m.countrySaveError)
    }
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={close}
      title={m.country}
      onOpenAutoFocus={isMobile ? focusSheetItself : undefined}
      className="max-w-md"
    >
      {/* `min-w-0`: окно — grid, и без него колонку раздвигала самая длинная
          `nowrap`-строка («Острова Святой Елены, Вознесения…») — `truncate` не
          срабатывал, у окна появлялась горизонтальная прокрутка, и Safari при
          автофокусе уводил окно вбок: заголовок срезан, поиск за правым краем. */}
      <div className="min-w-0 space-y-3 px-1 pb-1">
        <Input
          autoFocus={!isMobile}
          value={query}
          placeholder={m.countrySearch}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="max-h-[50dvh] space-y-1 overflow-y-auto">
          {isListLoading ? (
            <div role="status" className="flex justify-center py-4">
              <Loader2 aria-hidden="true" className="size-5 animate-spin text-muted-foreground" />
              <span className="sr-only">{m.countryLoading}</span>
            </div>
          ) : null}
          {isError ? (
            <div className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <p className="text-destructive">{m.countryLoadError}</p>
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                {m.retry}
              </Button>
            </div>
          ) : null}
          {shown.length === 0 && countries.length > 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">{m.countryEmpty}</p>
          ) : null}
          {shown.map((c) => {
            const selected = c.code === current
            return (
              <button
                key={c.code}
                type="button"
                aria-pressed={selected}
                disabled={isLoading}
                onClick={() => void pick(c.code)}
                className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-foreground/5 disabled:opacity-60"
              >
                <span className="min-w-0 truncate">{countryName(c, lang)}</span>
                {selected ? <Check className="size-4 shrink-0" /> : null}
              </button>
            )
          })}
        </div>
      </div>
    </ResponsiveDialog>
  )
}
